import { AppointmentPhase, Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import PublicationToWebIcon from "components/PublicationToWeb/PublicationToWebIcon";
import { FormSection, InPageEditableSectionField } from "components/MainSectionTemplate/types";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import ProductionModalPicker from "components/Picker/ProductionPicker/ProductionModalPicker";
import { BookkeepingSmallDetails } from "components/SmallDetails";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import VisibleToExternalIcon from "components/VisibleToExternal/VisibleToExternalIcon";
import WebsiteLink from "components/Website/WebsiteLink";
import PublicationDropdown from "components/dropdowns/reactselect/PublicationDropdown";
import UrlInput from "components/urlinput";
import { ShowDateAndTimePicker } from "components/showdatepicker/ShowDateAndTimePicker";
import { ShowDates } from "components/showdatepicker/models";
import React from "react";
import ReadonlyProduction from "show/detail/components/ReadonlyProduction";
import { ShowDateTimeShowInfo } from "show/components/ShowDateTime";
import { getInstructionForExternal, getInstructionForInternal, ShowFieldMetaData, ShowFormState } from "shared/utils/fieldsHelper/showFields";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { ShowTitle } from "show/detail/components/ShowTitle";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import moment from "moment";
import { CurrencyAndExchangeRate, CurrencyAndExchangeRatePicker, ICurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import { resolveBookkeepingChange } from "components/exchangerate/bookkeepingCurrencyUtils";
import { formatCurrency, getCurrency } from "utilities";
import { ProjectCodeInput } from "components/ProjectCodeInput";
import BookkeepingPaginatedDropdown from "components/dropdowns/paginated/BookkeepingPaginatedDropdown";
import { ExclusivityConflictsForShowSidePanel } from "exclusivityclause/ExclusivityConflictsForShowSidePanel";

interface SidePanelOptions {
  exclusivityClause: server.dto.ExclusivityClause
  products: server.dto.ProductsByParent
}

export const mainSection = (
  show: server.dto.Show,
  showInfo: ShowFormState,
  tags: server.dto.TagSmall[],
  isExternal: boolean,
  fieldMetaData: ShowFieldMetaData,
  sidePanel: SidePanelOptions = null
): FormSection => {
  const isInternal = !isExternal;
  const isSidePanel = sidePanel != null;
  const exclusivityFields: InPageEditableSectionField[] = isSidePanel && sidePanel.exclusivityClause != null ? [{
    key: "exclusivity",
    label: RESX.Exclusivity.resxExclusivity,
    hidden: isExternal,
    render: () => <ExclusivityConflictsForShowSidePanel
      show={show}
      exclusivityCheckFields={{
        artistId: showInfo.artist?.artistId,
        excludeShowId: show.showId,
        exclusivityClause: sidePanel.exclusivityClause,
        showStartDisplay: showInfo.showDates.startDisplayDate,
        showEndDisplay: showInfo.showDates.endDisplayDate,
        venueLocation: showInfo.venue?.location,
        venueId: showInfo.venue?.venueId,
        appointmentPhase: showInfo.status.appointmentPhase,
      }}
    />,
  }] : [];
  const sidePanelFinancialFields = (): InPageEditableSectionField[] => {
    if (!isSidePanel) return [];

    const products = sidePanel.products;
    const costsExTax = products.subtotal.costs?.amount;
    const salesExTax = products.subtotal.sales?.amount;
    const hasProducts = products.collection.length > 0;

    return [{
      key: "sales",
      label: RESX.Financial.Sales,
      isEmpty: !hasProducts,
      hidden: !site.permissions.show.canReadSales || !site.currentEnvironment.show.displaySales,
      render: () => <span>{formatCurrency(salesExTax, show.currency, serverReference.culture.uiLocale, { hideCents: true })}</span>
    }, {
      key: "costs",
      label: RESX.Financial.Costs,
      isEmpty: !hasProducts,
      hidden: !site.permissions.show.canReadCosts || !site.currentEnvironment.show.displayCosts,
      render: () => <span>{formatCurrency(costsExTax, show.currency, serverReference.culture.uiLocale, { hideCents: true })}</span>
    }];
  };

  // For fields editable in both environments, pick the matching update endpoint.
  // UpdateShowInternalEnvironmentInstruction is a superset of UpdateShowExternalEnvironmentInstruction,
  // so the internal partial can be used for both.
  const updateMainField = (
    partial: Partial<Omit<server.dto.UpdateShowInternalEnvironmentInstruction, "showId">>,
  ) =>
    isExternal
      ? window.CoreApi.Show.UpdateForExternalEnvironment(getInstructionForExternal(show.showId, showInfo, partial as any))
      : window.CoreApi.Show.UpdateForInternalEnvironment(getInstructionForInternal(show.showId, showInfo, partial));

  return {
    type: "regular",
    key: "showMainDetails",
    title: site.currentEnvironment.show.singular,
    fields: [
      {
        ...fieldMetaData.notValidated.fields(),
        key: "notValidated",
        label: RESX.Planning.ExternalInput,
        render: () => (
          <NotValidatedRow
            canApprove={show.permissions.canApprove}
            sublabel={`${RESX.Planning.InsertedBy} ${show.createdUser.fullName} ${moment.utc(show.createdUtc).fromNow()}`}
            approveEndpoint={() => window.CoreApi.Show.Approve({ showId: show.showId })}
          />
        ),
      },
      {
        ...fieldMetaData.tour.fields(),
        key: "production",
        label: site.currentEnvironment.tour.singular,
        render: () => <ReadonlyProduction production={showInfo.tour} />,
        inPageEdit: {
          hoverEdit: true,
          canRemove: true,
          initialValue: showInfo.tour,
          render: ({ onChange, cancel, value }) => (
            <div className="main-section-template-fields-padding">
              <ProductionModalPicker
                inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
                canCreateNew={site.permissions.tour.canCreate}
              />
            </div>
          ),
          type: "custom",
          submitChange: (value: server.dto.TourSmall) =>
            updateMainField({ productionId: value?.productionId ?? null }),
        }
      },
      {
        key: "showTitle",
        label: RESX.GeneralLabels.Title,
        render: () => <ShowTitle show={show} />
      },
      {
        ...fieldMetaData.dateTime.fields(),
        key: "showDates",
        label: RESX.DateTime.lblDate,
        render: () => <ShowDateTimeShowInfo showDates={showInfo.showDates} />,
        inPageEdit: {
          initialValue: showInfo.showDates,
          fullWidth: true,
          render: ({ value, onChange }) => (
            <div className="main-section-template-fields-padding">
              <ShowDateAndTimePicker
                timePickerPlaceholder={RESX.Show.TimeOfPerformance}
                showDates={value}
                onChange={(v, hasErrors, submit) => onChange(v, !hasErrors && submit)}
                compact={isSidePanel}
              />
            </div>
          ),
          type: "confirmOrCancelButton",
          submitChange: (value: ShowDates) =>
            updateMainField({
              dateTime: {
                startDisplay: value.startDisplayDate.toDate(),
                endDisplay: value.endDisplayDate.toDate(),
                customFallbackTime: value.timeOfPerformance,
                mode: value.mode,
              },
            })
        },
      }, {
        ...fieldMetaData.status.fields(),
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => (
          <div className="flex gap-small">
            <AppointmentPhase value={showInfo.status.appointmentPhase} displayLabel={false} />
            <Status status={showInfo.status} />
          </div>
        ),
        inPageEdit: {
          initialValue: showInfo.status,
          fullWidth: true,
          render: ({ value, onChange, cancel }) => (
            <StatusDropdown
              mainArea="show"
              options={undefined}
              value={value}
              onChange={v => onChange(v, true)}
              onEsc={() => cancel()}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true,
              }}
            />
          ),
          type: "custom",
          submitChange: (value: server.dto.StatusSmall) =>
            updateMainField({ statusId: value.statusId })
        },
      },
      ...exclusivityFields,
      {
        ...fieldMetaData.eventName.fields(),
        key: "eventName",
        label: RESX.Show.NameOfEvent,
        render: () => <>{showInfo.eventName}</>,
        inPageEdit: {
          initialValue: showInfo.eventName ?? "",
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }) => (
            <OptionalTextInput
              focusOnMount
              maxLength={serverReference.constants.show.maxEventNameLength}
              value={value}
              onChange={onChange}
              onEnter={submit}
              onEsc={cancel}
            />
          ),
          type: "confirmOrCancelButton",
          submitChange: (value: string) =>
            updateMainField({ eventName: value })
        }
      }, {
        ...fieldMetaData.eventWebsite.fields(),
        key: "eventWebsite",
        label: RESX.Show.EventWebsite,
        render: () => <WebsiteLink website={showInfo.eventWebsite} />,
        inPageEdit: {
          initialValue: showInfo.eventWebsite ?? "",
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }) => (
            <UrlInput
              value={value}
              onValueChange={onChange}
              validatorKey="eventWebsite"
              onEnter={submit}
              onEsc={cancel}
              focusOnMount
              maxLength={serverReference.constants.url.maxUrlLength}
            />
          ),
          type: "confirmOrCancelButton",
          submitChange: (value: string) =>
            updateMainField({ eventWebsite: value })
        }
      }, {
        ...fieldMetaData.publicationStatus.fields(),
        key: "publication",
        label: RESX.SelectionManager.Publication,
        render: () => (
          <div className="flex gap">
            {isInternal && <VisibleToExternalIcon visibleToExternal={showInfo.publicationStatus === "forInternalAndExternal" || showInfo.publicationStatus === "forInternalExternalAndWebOutput"} />}
            <PublicationToWebIcon publicationToWeb={showInfo.publicationStatus === "forInternalExternalAndWebOutput"} />
          </div>
        ),
        inPageEdit: {
          initialValue: showInfo.publicationStatus,
          fullWidth: true,
          render: ({ value, onChange }) => (
            <PublicationDropdown
              value={value}
              onChange={v => onChange(v, true)}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true,
              }}
            />
          ),
          type: "custom",
          submitChange: (value: server.dto.PublicationStatuses) =>
            window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, { publicationStatus: value })
            )
        }
      }, {
        ...fieldMetaData.tags.fields(),
        key: "tags",
        label: RESX.Tag.Tags,
        render: () => <TagList tags={tags} version="long" />,
        inPageEdit: {
          initialValue: tags,
          render: ({ value, onChange }) => (
            <TagsSelect
              mainArea="show"
              value={value}
              onChange={onChange}
              inPageEditing="edit"
            />
          ),
          type: "confirmOrCancelButton",
          submitChange: (value: server.dto.TagSmall[]) =>
            window.CoreApi.Show.UpdateTags({
              itemId: show.showId,
              tagsIds: value.map(t => t.tagId),
            })
        }
      }, {
        ...fieldMetaData.bookkeeping.fields(),
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        render: () => <BookkeepingSmallDetails bookkeeping={showInfo.bookkeeping} />,
        inPageEdit: {
          initialValue: showInfo.bookkeeping,
          fullWidth: true,
          render: ({ value, onChange }) => (
            <BookkeepingPaginatedDropdown
              value={value}
              onChange={v => onChange(v, true)}
              config={{ focusOnMount: true, openMenuOnFocus: true }}
            />
          ),
          type: "custom",
          submitChange: async (value: server.dto.BookkeepingSmall) => {
            const resolution = await resolveBookkeepingChange(
              value,
              showInfo.currency,
              showInfo.exchangeRate,
              true,
              showInfo.bookkeeping.baseCurrency
            );
            return updateMainField({ bookkeepingId: value.bookkeepingId, exchangeRate: resolution.exchangeRate });
          },
        }
      }, {
        ...fieldMetaData.currencyAndExchangeRate.fields(),
        key: "currencyAndExchangeRate",
        label: RESX.Financial.Currency,
        render: () => <CurrencyAndExchangeRate
          bookkeeping={showInfo.bookkeeping}
          currency={getCurrency(showInfo.currency)}
          exchangeRate={showInfo.exchangeRate}
        />,
        inPageEdit: {
          initialValue: { currency: showInfo.currency, exchangeRate: showInfo.exchangeRate },
          fullWidth: true,
          render: ({ value, onChange, cancel }) => (
            <CurrencyAndExchangeRatePicker
              focusOnMount
              validatorKey="currencyAndExchangeRate"
              bookkeeping={showInfo.bookkeeping}
              currency={value.currency}
              exchangeRate={value.exchangeRate}
              onChange={onChange}
              onEsc={() => cancel()}
              onEnter={v => onChange(v, true)}
            />
          ),
          type: "confirmOrCancelButton",
          submitChange: (values: ICurrencyAndExchangeRate) =>
            updateMainField({ currency: values.currency, exchangeRate: values.exchangeRate }),
        }
      }, {
        ...fieldMetaData.costCenter.fields(),
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        render: () => showInfo.costCenter
          ? <SmallDetails
            label={showInfo.costCenter.title}
            subLabel={showInfo.costCenter.internalId}
          />
          : <div className="s1_red">{RESX.GeneralLabels.Unknown}</div>,
        inPageEdit: {
          hoverEdit: true,
          initialValue: showInfo.costCenter,
          render: ({ onChange, cancel, value }) => (
            <div className="main-section-template-fields-padding">
              <CostCenterModalPicker
                inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
                canCreateNew={false}
              />
            </div>
          ),
          type: "custom",
          submitChange: (value: server.dto.CostCenter) =>
            window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, { costCenterId: value?.costCenterId ?? null })
            )
        }
      },
      ...sidePanelFinancialFields(),
      {
        ...fieldMetaData.projectCode.fields(),
        key: "projectCode",
        label: RESX.Show.ProjectCode,
        render: () => <>{showInfo.projectCode}</>,
        inPageEdit: {
          initialValue: site.currentEnvironment.projectCode === "displayBusinessCentral" ? undefined : (showInfo.projectCode ?? ""),
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }) => (
            <div className={site.currentEnvironment.projectCode === "displayBusinessCentral" ? "main-section-template-fields-padding" : ""}>
              <ProjectCodeInput
                focusOnMount
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                canCreateNew
              />
            </div>
          ),
          type: "confirmOrCancelButton",
          submitChange: (value: string) =>
            window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, { projectCode: value })
            )
        }
      }, {
        ...fieldMetaData.owner.fields(),
        key: "owner",
        label: RESX.GeneralLabels.Owner,
        render: () => <UserWithAvatar user={showInfo.owner} />,
        inPageEdit: {
          hoverEdit: true,
          initialValue: showInfo.owner,
          render: ({ onChange, cancel, value }) => (
            <div className="main-section-template-fields-padding">
              <UserModalPicker
                inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
                canChooseDeactivated
                canCreateNew={false}
              />
            </div>
          ),
          type: "custom",
          submitChange: (value: server.dto.UserSmall) =>
            window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, { ownerUserId: value.userId })
            )
        }
      }, {
        key: "account",
        label: RESX.Account.resxAccount,
        render: () => <>{show.client.name}</>,
      }]
  };
};
